/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { Button } from '@mui/material';
import { DataType } from '@grapecity/wijmo';
import { DataMap, CellType } from '@grapecity/wijmo.grid';
import { getMapZones, setMapZones } from 'apis/setup/mobile/MapZone';
import { deleteZones } from 'apis/setup/mobile/BoxLocation';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { MapZone } from 'models/setup/mobile/MapZone';
import { CommonYN } from 'models/common/Common';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { CrudCode } from 'models/common/Edit';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { IconButton } from 'components/buttons/IconSVG';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { getExcelFileName } from 'utils/ExcelUtil';

interface SearchParamData {
  sPrjId?: string;
  sZonNm?: string;
  sUseYn?: string;
  editable: boolean;
  callBack?: any;
  searchParamData?: any;
  searchState?: any;
  projectOptions?: { cmnCd: string; cmnCdNm: string }[];
}

const ZoneGroupGrid = (props: SearchParamData) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const [flexRef, setflexRef] = useState<any>();
  const [rowData, setRowData] = useState<MapZone[]>([]);
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();

  const useYNList: { value: string; label: string }[] = [
    { value: 'Y', label: '사용' },
    { value: 'N', label: '미사용' },
  ];

  const prjZonYnList: { value: string; label: string }[] = [
    { value: 'Y', label: 'O' },
    { value: 'N', label: 'X' },
  ];

  // 프로젝트 옵션 (ComboBox용)
  const projectNames = useMemo(() => {
    return props.projectOptions?.map((p) => ({
      cmnCd: p.cmnCdNm,
      cmnCdNm: p.cmnCdNm,
    }));
  }, [props.projectOptions]);

  const layoutDefinition = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      width: 40,
      isReadOnly: true,
      cellTemplate: (grid) => grid.row._idx + 1,
    },
    {
      binding: 'crudKey',
      header: String(t('com.label.상태', '상태')),
      width: 47,
      isReadOnly: true,
      cellTemplate: GridStatusCellTemplate,
    },
    {
      binding: 'detail',
      header: String(t('com.label.상세', '상세')),
      width: 45,
      isReadOnly: true,
      cssClass: 'WijmoPlay',
      cellTemplate: (params) =>
        params.item['crudKey'] === CrudCode.CREATE || params.item['crudKey'] === CrudCode.DELETE
          ? ''
          : '<Button id="btnDetail"></Button>',
    },
    // 프로젝트명 컬럼 안보이게 수정
    // {
    //   header: String(t('setup.grid.프로젝트명', '프로젝트명')),
    //   binding: 'elmPrjNm',
    //   width: 250,
    //   align: 'left',
    //   dataMap: new DataMap(projectNames, 'cmnCd', 'cmnCdNm'),
    //   isReadOnly: true,
    // },
    {
      header: String(t('setup.grid.구역코드', '구역코드')),
      binding: 'mapZonId',
      minWidth: 70,
      width: 120,
      align: 'left',
      isReadOnly: true,
    },
    {
      header: String(t('setup.grid.구역명', '구역명')),
      binding: 'zonNm',
      width: 200,
      align: 'left',
    },
    {
      header: String(t('setup.grid.구역상세', '구역상세')),
      binding: 'zonDesc',
      minWidth: 80,
      width: '*',
      align: 'left',
    },
    {
      header: String(t('com.label.대표구역여부', '대표구역여부')),
      binding: 'prjZonYn',
      minWidth: 80,
      width: 120,
      align: 'center',
      dataMap: new DataMap(prjZonYnList, 'value', 'label'),
    },
    {
      header: String(t('com.label.사용여부', '사용여부')),
      binding: 'useYn',
      minWidth: 60,
      width: 90,
      align: 'center',
      dataMap: new DataMap(useYNList, 'value', 'label'),
    },
    {
      header: String(t('setup.grid.포인트수', '포인트수')),
      binding: 'pointCnt',
      minWidth: 60,
      width: 90,
      align: 'center',
      dataType: DataType.Number,
      isReadOnly: true,
    },
    {
      header: String(t('com.label.등록자', '등록자')),
      binding: 'dataInsUserNm',
      width: 120,
      align: 'center',
      isReadOnly: true,
    },
    {
      header: String(t('com.label.등록일시', '등록일시')),
      binding: 'dataInsDtm',
      width: 160,
      align: 'center',
      isReadOnly: true,
    },
    {
      header: String(t('com.label.수정자', '수정자')),
      binding: 'dataUpdUserNm',
      width: 120,
      align: 'center',
      isReadOnly: true,
    },
    {
      header: String(t('com.label.수정일시', '수정일시')),
      binding: 'dataUpdDtm',
      width: 160,
      align: 'center',
      isReadOnly: true,
    },
  ];

  const btnExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = 'zones';
    book.saveAsync(getExcelFileName(t('setup.label.구역목록', '구역목록')));
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      const isReadOnly = item.crudKey !== CrudCode.CREATE;

      // 구역코드
      if ('zonCd' === binding) {
        cell.ariaReadOnly = isReadOnly;
      }
    }
  });

  const onInitialized = (grid) => {
    setflexRef(grid);
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row].dataItem;

      if (e.target instanceof HTMLButtonElement) {
        switch (e.target.id) {
          // 상세보기
          case 'btnDetail':
            onCellClicked({ ...grid.rows[ht.row].dataItem, crudKey: CrudCode.READ });
            break;
        }
      }
    });
  };

  const fnSearchZone = (searchPrjId: string, searchZonNm: string, searchUseYn: string) => {
    getMapZones(searchPrjId, searchZonNm, searchUseYn).then((result: MapZone[]) => {
      setRowData(result);
    });
  };

  const btnAddRow = useCallback(() => {
    // 현재 선택된 프로젝트의 이름 찾기
    const selectedPrjName = props.projectOptions?.find((p) => p.cmnCd === props.sPrjId)?.cmnCdNm;

    // 프로젝트 및 대표구역여부 기본값 추가
    const newRow = {
      crudKey: CrudCode.CREATE,
      elmPrjId: props.sPrjId,
      elmPrjNm: selectedPrjName,
      prjZonYn: CommonYN.N,
      useYn: CommonYN.Y,
    } as MapZone;

    setRowData([newRow, ...rowData]);
  }, [rowData]);

  const btnDelRow = useCallback(() => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
    if (selectedRows.length < 1) {
      openMessageBar({
        type: 'error',
        content: t('setup.msg.삭제할 구역을 선택해 주세요.', '삭제할 구역을 선택해 주세요.'),
      });
      return;
    }

    const newRowData = [...rowData];
    selectedRows.forEach((row) => {
      const item = row.dataItem;
      if (item.crudKey === CrudCode.CREATE) {
        const idx = newRowData.indexOf(item);
        if (idx > -1) newRowData.splice(idx, 1);
      } else {
        item.crudKey = CrudCode.DELETE;
      }
    });
    setRowData(newRowData);
  }, [rowData, t, openMessageBar]);

  const btnCancelDelRow = useCallback(() => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
    const deleteSelectedRows = selectedRows.filter((r) => r.dataItem.crudKey === CrudCode.DELETE);

    if (deleteSelectedRows.length < 1) {
      openMessageBar({
        type: 'error',
        content: t(
          'setup.msg.삭제취소할 구역을 선택해 주세요.',
          '삭제취소할 구역을 선택해 주세요.'
        ),
      });
      return;
    }

    const newRowData = [...rowData];
    deleteSelectedRows.forEach((row) => {
      const item = row.dataItem;
      item.crudKey = null;
    });
    setRowData(newRowData);
  }, [rowData, t, openMessageBar]);

  const hasDeleteRows = rowData.some((r) => r.crudKey === CrudCode.DELETE);

  const btnSave = () => {
    // DELETE 상태가 아닌 행만 validation
    const valid = rowData
      .filter((r) => r.crudKey !== CrudCode.DELETE)
      .map((rowNode, index) => {
        if (rowNode.zonNm == null || rowNode.zonNm == '') {
          return `${index + 1} : ${t(
            'setup.msg.구역명을 입력해주세요.',
            '구역명을 입력해주세요.'
          )}`;
        }

        // 프로젝트 컬럼 검증
        if (rowNode.elmPrjNm == null || rowNode.elmPrjNm == '') {
          return `${index + 1} : ${t(
            'setup.msg.프로젝트명을 선택해주세요.',
            '프로젝트명을 선택해주세요.'
          )}`;
        }

        // 대표구역여부 검증
        if (rowNode.elmPrjNm == null || rowNode.elmPrjNm == '') {
          return `${index + 1} : ${t(
            'setup.msg.대표구역여부를 선택해주세요.',
            '대표구역여부를 선택해주세요.'
          )}`;
        }
      })
      .filter((element) => element !== undefined);
    if (valid?.length) {
      openMessageBar({ type: 'error', content: valid.toString() });
      return false;
    }

    // 대표구역여부 카운트(O 갯수) - 검증용
    let prjZonCount = 0;

    // 저장 시, 대표구역여부 개수 계산
    rowData.forEach((row) => {
      console.log(row);
      if (row.prjZonYn === CommonYN.Y) {
        prjZonCount = row.crudKey === CrudCode.DELETE ? prjZonCount - 1 : prjZonCount + 1;
      }
    });

    // 대표구역여부가 1개 초과일 경우 에러메시지 발생
    if (prjZonCount > 1) {
      openMessageBar({
        type: 'error',
        content: `${t(
          'setup.msg.대표구역이 2개 이상 존재합니다.',
          '대표구역을 하나만 지정해주세요.'
        )}`,
      });
      return false;
    }

    // CREATE/UPDATE 행
    const saveRows = rowData
      .filter((r) => r.crudKey === CrudCode.CREATE || r.crudKey === CrudCode.UPDATE)
      .map((rowNode) => {
        const option = props.projectOptions?.find((opt) => opt.cmnCdNm === rowNode.elmPrjNm);
        return {
          ...rowNode,
          elmPrjId: option?.cmnCd || rowNode.elmPrjId,
        };
      });

    // DELETE 행
    const deleteRows = rowData.filter((r) => r.crudKey === CrudCode.DELETE);

    if (saveRows.length === 0 && deleteRows.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.label.수정된 항목이 없습니다.', '수정된 항목이 없습니다.'),
      });
      return false;
    }

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: async () => {
        try {
          const promises: Promise<any>[] = [];
          if (saveRows.length > 0) promises.push(setMapZones(saveRows));
          if (deleteRows.length > 0)
            promises.push(
              deleteZones(deleteRows.map((r) => r.mapZonId).filter((id): id is string => !!id))
            );

          await Promise.all(promises);
          openMessageBar({
            type: 'confirm',
            content: t('com.label.저장되었습니다.', '저장되었습니다.'),
          });
          fnSearchZone(props.sPrjId || '', props.sZonNm || '', props.sUseYn || '');
        } catch (error) {
          openMessageBar({
            type: 'error',
            content: t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
          });
        }
      },
      noCallback: () => {
        return false;
      },
    });
  };

  const onCellClicked = (params: any) => {
    props.callBack &&
      params.crudKey != CrudCode.CREATE &&
      props.callBack(params.mapZonId, params.zonCd, params.zonNm);
  };

  useEffect(() => {
    if (props.searchParamData != false) {
      fnSearchZone(props.sPrjId || '', props.sZonNm || '', props.sUseYn || '');
    }
  }, [props.sPrjId, props.sZonNm, props.sUseYn, props.searchParamData, props.searchState]);

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.구역목록', '구역목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        {props.editable && (
          <ControlBtnGroup>
            <Button
              css={IconButton.button}
              className="Exceldown"
              onClick={btnExcelExport}
              disableRipple
            >
              {t('com.button.다운로드', '화면 다운로드')}
            </Button>
            <Button css={IconButton.button} className="addRow" disableRipple onClick={btnAddRow}>
              {t('com.button.행추가', '행추가')}
            </Button>
            <Button css={IconButton.button} className="delRow" disableRipple onClick={btnDelRow}>
              {t('com.button.행삭제', '행삭제')}
            </Button>
            {hasDeleteRows && (
              <Button
                css={IconButton.button}
                className="delRow"
                disableRipple
                onClick={btnCancelDelRow}
              >
                {t('com.button.행삭제취소', '행삭제취소')}
              </Button>
            )}
          </ControlBtnGroup>
        )}
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={250}
        deferResizing={false}
        initialized={onInitialized}
        excludePin={['detail']}
        excludeFilter={['detail']}
        align={'center'}
        beginningEdit={(grid, e) => {
          const binding = grid.columns[e.col].binding;
          const item = grid.rows[e.row].dataItem;
          // readonly 설정
          // 프로젝트명
          if (['elmPrjNm'].includes(binding)) {
            if (CrudCode.CREATE !== item.crudKey) {
              e.cancel = true;
            }
          }
          // 구역코드
          if (['zonCd'].includes(binding)) {
            if (CrudCode.CREATE !== item.crudKey) {
              e.cancel = true;
            }
          }
        }}
      />
      <GlobalBtnGroup>
        {props.editable && (
          <Button css={IconButton.button} className="save" disableRipple onClick={btnSave}>
            {t('com.button.저장', '저장')}
          </Button>
        )}
      </GlobalBtnGroup>
    </>
  );
};

export default ZoneGroupGrid;
